import React from 'react';
import { motion } from 'framer-motion';
import { Lock, ArrowRight, Brain } from 'lucide-react';
import type { AuthGateProps } from '../../lib/utils/types';

export function AuthGate({ children, title, description }: AuthGateProps) {
  // This would normally check your auth state from a context or store
  const isAuthenticated = false; // Replace with actual auth check

  if (isAuthenticated) {
    return <>{children}</>;
  }

  const defaultDescription = title ? defaultDescriptions[title] : undefined;
  const trialMessage = title
    ? trialMessages[title]
    : 'Experience the power of AI-driven financial management with our 7-day trial.';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="relative bg-dark-card rounded-lg p-8 text-center overflow-hidden"
    >
      {/* Background Gradient Effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-600/5 via-transparent to-transparent" />

      <div className="relative">
        <div className="flex justify-center mb-6">
          <div className="bg-primary-400/10 p-4 rounded-full">
            <Brain className="h-8 w-8 text-primary-400" />
          </div>
        </div>
        <h3 className="text-xl font-semibold text-dark-text mb-2">
          {title || 'Unlock AI-Powered Features'}
        </h3>
        {description && <p className="text-dark-muted mb-4 text-sm">{description}</p>}
        <p className="text-dark-muted mb-6 max-w-md mx-auto">
          {trialMessage}
          <span className="block mt-2 text-sm">
            Credit card required, cancel anytime during trial.
          </span>
        </p>
        <motion.a
          href="#pricing"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="inline-flex items-center justify-center px-6 py-3 bg-primary-600 text-dark-text rounded-lg hover:bg-primary-700 transition-colors shadow-glow"
        >
          Start Free Trial
          <ArrowRight className="ml-2 h-4 w-4" />
        </motion.a>
        <div className="mt-6 flex items-center justify-center gap-4 text-sm text-dark-muted">
          <div className="flex items-center">
            <Lock className="h-4 w-4 mr-1" />
            <span>Bank-grade security</span>
          </div>
          <span>•</span>
          <span>7-day trial</span>
          <span>•</span>
          <span>Cancel anytime</span>
        </div>
      </div>
    </motion.div>
  );
}

const trialMessages: Record<string, string> = {
  'AI Financial Insights Dashboard':
    'Transform your financial decision-making with AI-powered insights. Start your 7-day trial to unlock real-time analytics and smart recommendations.',
  'Smart Receipt Analysis':
    'Save hours on expense management with AI-powered receipt scanning and categorization. Try it free for 7 days.',
  'AI Financial Assistant':
    'Get personalized financial guidance powered by advanced AI. Experience your own financial co-pilot with our 7-day trial.',
  'Smart Contract Analysis':
    'Analyze contracts in seconds with AI-powered risk assessment. See the power of intelligent contract review free for 7 days.',
  'AI Support Assistant':
    'Get instant answers to your financial questions 24/7. Start your 7-day trial of our AI support system.',
  'Document Scanner & Analysis':
    'Turn paperwork into actionable data instantly. Experience the future of document processing free for 7 days.',
  'AI Financial Forecasting':
    'Predict your financial future with machine learning. Unlock AI-powered forecasting with our 7-day trial.',
  'Smart Job Quote Management':
    'Create winning quotes faster with AI-assisted pricing. Try our intelligent quote system free for 7 days.',
  'Expense Tracking & Analysis':
    'Take control of your expenses with AI categorization. Start optimizing your spending with our 7-day trial.',
  'Equipment Management System':
    'Optimize equipment maintenance with predictive AI. Experience smarter asset management free for 7 days.',
  'Smart Time Tracking':
    'Track time intelligently with automated categorization. Transform your time management with our 7-day trial.',
  'Intelligent Invoice Management':
    'Automate your entire invoicing workflow with AI. Streamline your billing process free for 7 days.',
  'Smart Payroll System':
    'Process payroll faster with intelligent automation. Experience error-free payroll with our 7-day trial.',
};

const defaultDescriptions: Record<string, string> = {
  'AI Financial Insights Dashboard':
    'Get real-time insights into your financial health with AI-powered analytics and recommendations.',
  'Smart Receipt Analysis':
    'Automatically extract and categorize data from receipts using advanced AI recognition.',
  'AI Financial Assistant': 'Your personal AI advisor for financial decisions and planning.',
  'Smart Contract Analysis': 'AI-powered contract review and risk assessment in seconds.',
  'AI Support Assistant': '24/7 intelligent support for all your financial questions.',
  'Document Scanner & Analysis':
    'Turn physical documents into actionable financial data instantly.',
  'AI Financial Forecasting': 'Predict future financial trends with machine learning algorithms.',
  'Smart Job Quote Management': 'Create and manage professional quotes with AI-assisted pricing.',
  'Expense Tracking & Analysis':
    'Track and optimize expenses with AI-powered categorization and insights.',
  'Equipment Management System': 'Smart tracking and maintenance predictions for your equipment.',
  'Smart Time Tracking': 'Intelligent time tracking with automated categorization and reporting.',
  'Intelligent Invoice Management': 'Automate your entire invoicing workflow with AI assistance.',
  'Smart Payroll System':
    'Streamline payroll processing with intelligent automation and compliance.',
};